import type {
  UseHasCompanyDocumentsParams,
  UseHasCompanyDocumentsResult,
} from './useHasCompanyDocuments.types';

import { useDocuments } from '../use-documents/useDocuments';

/**
 * Checks whether an organization has any company (Customer) documents.
 *
 * Reuses the same React Query cache key as `useDocuments`, so navigating
 * to the Company Documents page afterwards is a free cache hit.
 *
 * When `enabled` is false, no query is created — `organizationId` is
 * withheld from `useDocuments` so its internal `enabled` stays false.
 *
 * Returns `hasDocuments: false` while loading or on error — callers that
 * need to distinguish loading from "no data" should check `isLoading`.
 */
export function useHasCompanyDocuments({
  organizationId,
  enabled = true,
}: UseHasCompanyDocumentsParams): UseHasCompanyDocumentsResult {
  const isQueryEnabled = enabled && !!organizationId;

  const { data, isLoading, isError } = useDocuments({
    documentType: 'Customer',
    organizationId: isQueryEnabled ? organizationId : undefined,
  });

  return {
    hasDocuments:
      isQueryEnabled && !isLoading && !isError && (data?.totalCount ?? 0) > 0,
    isLoading: isQueryEnabled && isLoading,
  };
}
